import { ReactElement, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { HiOutlineChevronRight } from 'react-icons/hi2';
import toast from 'react-hot-toast';
import FormCard from '../../components/FormCard';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import {
  useDeleteEpisodeCache,
  useEpisodeSummary,
  useExportEpisodeTableCsv,
  useExportEpisodeTableXlsx,
} from '../../hooks/useEpisodeDbCache';
import { useUser } from '../../hooks/useUser';
import { EpisodeTableSummary } from '../../api/episodeDbCache';
import { isAdmin } from '../../utils/userRole';
import EpisodeRunsPanel from './EpisodeRunsPanel';

const ALL_TABLES = [
  'episodes',
  'runs',
  'run_metadata',
  'ant_packets',
  'ble_frames',
  'internal_packets',
] as const;

interface TableSectionProps {
  name: string;
  summary: EpisodeTableSummary | undefined;
  s3Key: string | undefined;
}

function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function base64ToBlob(b64: string, type: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

interface AntPacketsExportProps {
  s3Key: string;
  groups: NonNullable<EpisodeTableSummary['groups']>;
}

function AntPacketsExport({ s3Key, groups }: AntPacketsExportProps): ReactElement {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const csvExport = useExportEpisodeTableCsv();
  const xlsxExport = useExportEpisodeTableXlsx();

  const options = useMemo(
    () =>
      groups.map((g, i) => ({
        idx: i,
        device_number: g.device_number as number | string,
        device_type: g.device_type as number | string,
        count: g.count,
      })),
    [groups],
  );

  const selected = options[selectedIdx];
  const filters = selected
    ? {
        device_number: selected.device_number,
        device_type: selected.device_type,
      }
    : {};

  const handleCsv = () => {
    if (!selected) return;
    csvExport.mutate(
      { s3Key, table: 'ant_packets', filters },
      {
        onSuccess: (res) => {
          downloadBlob(res.filename, new Blob([res.csv], { type: 'text/csv' }));
          toast.success(`Exported ${res.row_count} packets`);
        },
        onError: (err) => toast.error(`CSV export failed: ${(err as Error).message}`),
      },
    );
  };

  const handleXlsx = () => {
    if (!selected) return;
    xlsxExport.mutate(
      { s3Key, table: 'ant_packets', filters },
      {
        onSuccess: (res) => {
          const blob = base64ToBlob(
            res.content_b64,
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          );
          downloadBlob(res.filename, blob);
          toast.success(`Exported ${res.row_count} packets (sheet per page)`);
        },
        onError: (err) => toast.error(`XLSX export failed: ${(err as Error).message}`),
      },
    );
  };

  const busy = csvExport.isPending || xlsxExport.isPending;

  return (
    <div className="cr-mt-3 cr-flex cr-flex-col cr-gap-2 cr-rounded-md cr-border cr-border-gray-200 cr-bg-gray-50 cr-p-3">
      <span className="cr-text-xs cr-font-semibold cr-text-gray-600">Queries</span>
      <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-2">
        <label htmlFor="ant-device-select" className="cr-text-xs cr-text-gray-700">
          Device:
        </label>
        <select
          id="ant-device-select"
          value={selectedIdx}
          onChange={(e) => setSelectedIdx(Number(e.target.value))}
          className="cr-rounded-md cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-1 cr-text-xs"
        >
          {options.map((o) => (
            <option key={o.idx} value={o.idx}>
              {String(o.device_number)} (type {String(o.device_type)}, {o.count} packets)
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleCsv}
          disabled={busy || !selected}
          className="cr-rounded-md cr-border cr-border-blue-300 cr-bg-white cr-px-3 cr-py-1 cr-text-xs cr-font-medium cr-text-blue-700 hover:cr-bg-blue-50 disabled:cr-cursor-not-allowed disabled:cr-opacity-60"
          title="Time-ordered CSV of every packet for the selected device"
        >
          {csvExport.isPending ? 'Exporting…' : 'Export CSV'}
        </button>
        <button
          type="button"
          onClick={handleXlsx}
          disabled={busy || !selected}
          className="cr-rounded-md cr-border cr-border-green-300 cr-bg-white cr-px-3 cr-py-1 cr-text-xs cr-font-medium cr-text-green-700 hover:cr-bg-green-50 disabled:cr-cursor-not-allowed disabled:cr-opacity-60"
          title="XLSX with an 'all' sheet plus one sheet per ANT page number"
        >
          {xlsxExport.isPending ? 'Exporting…' : 'Export XLSX'}
        </button>
      </div>
    </div>
  );
}

function renderRows(rows: Record<string, unknown>[] | undefined): ReactElement {
  if (!rows || rows.length === 0) {
    return <p className="cr-text-sm cr-text-gray-500">No rows.</p>;
  }
  const cols = Object.keys(rows[0]);
  return (
    <div className="cr-overflow-x-auto">
      <table className="cr-min-w-full cr-text-xs">
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c}
                className="cr-border-b cr-border-gray-200 cr-px-2 cr-py-1 cr-text-left cr-font-semibold"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 === 0 ? 'cr-bg-white' : 'cr-bg-gray-50'}>
              {cols.map((c) => (
                <td key={c} className="cr-border-b cr-border-gray-100 cr-px-2 cr-py-1">
                  {formatCell(r[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function TableSection({ name, summary, s3Key }: TableSectionProps): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const present = summary?.present ?? false;
  const rowCount = summary?.row_count ?? 0;
  const kind = summary?.kind;

  return (
    <FormCard>
      <button
        className="cr-flex cr-w-full cr-items-center cr-justify-between cr-p-3 cr-text-left hover:cr-bg-gray-50"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="cr-flex cr-items-center cr-gap-3">
          <HiOutlineChevronRight
            className={`cr-transition-transform cr-duration-200 ${
              expanded ? 'cr-rotate-90' : ''
            }`}
            size={18}
          />
          <span className="cr-font-semibold">{name}</span>
          <span className="cr-text-xs cr-text-gray-500">
            {present ? `${rowCount} row${rowCount === 1 ? '' : 's'}` : 'not present'}
          </span>
        </span>
        {kind && (
          <span className="cr-rounded-full cr-bg-gray-100 cr-px-2 cr-py-0.5 cr-text-xs cr-text-gray-600">
            {kind}
          </span>
        )}
      </button>
      {expanded && (
        <div className="cr-border-t cr-border-gray-200 cr-p-3">
          {!present && (
            <p className="cr-text-sm cr-text-gray-500">
              Table is not present in this episode .db file.
            </p>
          )}
          {present && kind === 'short' && renderRows(summary?.rows)}
          {present && kind === 'large' && (
            <div className="cr-flex cr-flex-col cr-gap-2">
              <div className="cr-text-xs cr-text-gray-600">
                Row count: {rowCount}
                {summary?.ts_min_ms != null && (
                  <>
                    {' '}· ts range: {summary.ts_min_ms} → {summary.ts_max_ms}
                  </>
                )}
              </div>
              {summary?.groups && summary.groups.length > 0 && (
                <>
                  <span className="cr-text-xs cr-font-semibold cr-text-gray-500">
                    Distinct sources ({summary.groups.length})
                  </span>
                  {renderRows(summary.groups)}
                </>
              )}
              <span className="cr-mt-2 cr-text-xs cr-font-semibold cr-text-gray-500">
                Sample rows
              </span>
              {renderRows(summary?.sample_rows)}
              {name === 'ant_packets' && s3Key && summary?.groups && summary.groups.length > 0 && (
                <AntPacketsExport s3Key={s3Key} groups={summary.groups} />
              )}
            </div>
          )}
        </div>
      )}
    </FormCard>
  );
}

/**
 * The file's own SQLite tables, read through the server's 48-hour cache.
 * Admins only, as the endpoints behind it are: they serve any team's file and
 * keep its bytes on the server, which is a diagnostic's reach, not a user's.
 */
function RawTables({ s3Key }: { s3Key: string | undefined }): ReactElement {
  const { data, isPending, isFetching, isError, error } = useEpisodeSummary(s3Key);
  const deleteCache = useDeleteEpisodeCache();

  const handleDeleteCache = () => {
    if (!s3Key) return;
    deleteCache.mutate(s3Key, {
      onSuccess: (res) => {
        if (res.evicted) {
          toast.success('Cache cleared — next view will re-download');
        } else {
          toast('No cache entry to clear');
        }
      },
      onError: (err) => toast.error(`Failed to clear cache: ${(err as Error).message}`),
    });
  };

  return (
    <>
      <SectionHeader title="Raw tables" variant="section" isLoading={isFetching} />

      {isPending && (
        <FormCard>
          <div className="cr-flex cr-items-center cr-gap-2 cr-p-3 cr-text-sm cr-text-gray-600">
            <Spinner isFetching />
            <span>Downloading and parsing episode .db…</span>
          </div>
        </FormCard>
      )}

      {isError && (
        <FormCard>
          <p className="cr-p-3 cr-text-sm cr-text-red-600">
            Failed to load episode: {(error as Error)?.message ?? 'unknown error'}
          </p>
        </FormCard>
      )}

      {data && (
        <>
          <FormCard>
            <div className="cr-flex cr-items-start cr-justify-between cr-gap-3 cr-p-3 cr-text-sm">
              <div className="cr-flex cr-flex-col cr-gap-1">
                <div>
                  <span className="cr-font-semibold">Device:</span>{' '}
                  {data.device_id ?? '(unknown)'}
                </div>
                <div>
                  <span className="cr-font-semibold">Captured:</span>{' '}
                  {data.captured_ts.replace('T', ' ').slice(0, 19)} UTC
                </div>
                <div className="cr-text-xs cr-text-gray-500">
                  {data.source === 'cache' ? 'Loaded from cache' : 'Just downloaded'}{' '}
                  · expires {data.expires_at.replace('T', ' ').slice(0, 19)} UTC
                </div>
              </div>
              <button
                onClick={handleDeleteCache}
                disabled={deleteCache.isPending}
                className="cr-shrink-0 cr-rounded-md cr-border cr-border-red-300 cr-bg-white cr-px-3 cr-py-1.5 cr-text-xs cr-font-medium cr-text-red-700 hover:cr-bg-red-50 disabled:cr-cursor-not-allowed disabled:cr-opacity-60"
                title="Remove this episode from the server-side cache; next view re-downloads from S3"
              >
                {deleteCache.isPending ? 'Clearing…' : 'Delete cache'}
              </button>
            </div>
          </FormCard>

          {ALL_TABLES.map((name) => (
            <TableSection
              key={name}
              name={name}
              summary={data.tables[name]}
              s3Key={s3Key}
            />
          ))}
        </>
      )}
    </>
  );
}

/**
 * Account → Recordings → one file. For everyone: which of the file's runs
 * the server has, and a way to import the ones it does not. For admins, the
 * raw table viewer underneath.
 */
export default function EpisodeDetailView(): ReactElement {
  const params = useParams<Record<string, string>>();
  const s3Key = params['*'];
  const [userProfile] = useUser();

  return (
    <>
      <SectionHeader title="Recording" backLink={{ to: '/account/episodes', text: 'Back to list' }} />

      <FormCard>
        <div className="cr-p-3 cr-text-left cr-text-sm">
          <span className="cr-font-semibold">Filename:</span> {s3Key?.split('/').pop()}
        </div>
      </FormCard>

      <EpisodeRunsPanel s3Key={s3Key} />

      {isAdmin(userProfile) && <RawTables s3Key={s3Key} />}
    </>
  );
}
